import { useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { GitMerge, Link2, Mail, Plus, Trash2, Eye } from "lucide-react";

const CONF_COLORS = { High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600" };

export function DuplicatesTab() {
  const { t } = useApp();
  const [sugs, setSugs] = useState(null);
  const [merging, setMerging] = useState(null);

  const load = () => api.get("/dedupe/suggestions").then((r) => setSugs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const doMerge = async (survivorId, mergedId) => {
    try {
      const r = await api.post("/dedupe/merge", { survivor_id: survivorId, merged_id: mergedId });
      toast.success(`${t("merged_ok")}: ${r.data.survivor}`);
      setMerging(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const doLink = async (s) => {
    try {
      await api.post("/dedupe/link", { a_id: s.a.id, b_id: s.b.id, type: "HQ ↔ Local Site" });
      toast.success(t("link_as_network") + " ✓");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!sugs) return <p className="text-sm text-gray-400">{t("loading")}</p>;
  return (
    <div className="space-y-3" data-testid="duplicates-tab">
      {sugs.length === 0 && <p className="text-sm text-gray-400">–</p>}
      {sugs.map((s, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-4" data-testid="duplicate-suggestion">
          <div className="flex flex-wrap items-center gap-3">
            <Badge className={`${CONF_COLORS[s.confidence]} hover:bg-inherit font-mono`}>{s.confidence} · {s.score}</Badge>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {[s.a, s.b].map((acc) => (
                <div key={acc.id} className="border border-gray-100 rounded-md p-2">
                  <p className="font-semibold text-[#1B2A4A]">{acc.name}</p>
                  <p className="text-[11px] text-gray-400 font-mono">{acc.account_number} · {acc.city} · {acc.vat_id || "–"}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => setMerging(s)} data-testid={`merge-btn-${i}`}>
                <GitMerge className="h-3.5 w-3.5 mr-1" />{t("merge")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => doLink(s)} data-testid={`link-btn-${i}`}>
                <Link2 className="h-3.5 w-3.5 mr-1" />{t("link_as_network")}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">{s.reasons.join(" · ")}</p>
        </div>
      ))}

      <Dialog open={!!merging} onOpenChange={(o) => !o && setMerging(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("pick_survivor")}</DialogTitle></DialogHeader>
          {merging && (
            <div className="space-y-2">
              {[[merging.a, merging.b], [merging.b, merging.a]].map(([survivor, merged]) => (
                <button key={survivor.id} data-testid={`survivor-${survivor.id}`}
                  onClick={() => doMerge(survivor.id, merged.id)}
                  className="w-full text-left border border-gray-200 rounded-md p-3 hover:border-[#F5A623] hover:bg-amber-50/50">
                  <p className="text-sm font-semibold">{survivor.name} <span className="text-emerald-600 text-xs">✓ {t("merge")}</span></p>
                  <p className="text-[11px] text-gray-400">{merged.name} → wird zusammengeführt</p>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MatrixTab() {
  const { t } = useApp();
  const [users, setUsers] = useState([]);
  const [locs, setLocs] = useState([]);
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => ["sales_rep", "sales_manager", "customer_service"].includes(u.role)))).catch(() => {});
    api.get("/locations?limit=1000").then((r) => setLocs(r.data)).catch(() => {});
  }, []);

  const assigned = (u) => dirty[u.id]?.location_ids ?? (u.assigned_location_ids || []);
  const toggle = (u, lid) => {
    const cur = assigned(u);
    const next = cur.includes(lid) ? cur.filter((x) => x !== lid) : [...cur, lid];
    setDirty((d) => ({ ...d, [u.id]: { ...d[u.id], location_ids: next } }));
  };
  const setDeputy = (u, did) => setDirty((d) => ({ ...d, [u.id]: { ...d[u.id], location_ids: assigned(u), deputy_user_id: did === "none" ? "" : did } }));

  const save = async () => {
    try {
      for (const [uid, body] of Object.entries(dirty)) {
        await api.put(`/users/${uid}/locations`, body);
      }
      toast.success(t("save") + " ✓");
      setDirty({});
      const r = await api.get("/users");
      setUsers(r.data.filter((u) => ["sales_rep", "sales_manager", "customer_service"].includes(u.role)));
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-3" data-testid="matrix-tab">
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="text-xs w-full">
          <thead>
            <tr className="border-b bg-gray-50/60">
              <th className="px-3 py-2 text-left sticky left-0 bg-gray-50">{t("owner")}</th>
              <th className="px-3 py-2 text-left">{t("deputy")}</th>
              {locs.map((l) => (
                <th key={l.id} className="px-2 py-2 text-center whitespace-nowrap" title={`${l.account_name} · ${l.country}`}>
                  <span className="[writing-mode:vertical-rl] rotate-180 inline-block max-h-28 overflow-hidden text-[10px] text-gray-500">{l.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-gray-50" data-testid={`matrix-row-${u.email}`}>
                <td className="px-3 py-2 font-medium sticky left-0 bg-white whitespace-nowrap">{u.name}<p className="text-[10px] text-gray-400">{u.territory}</p></td>
                <td className="px-2 py-2">
                  <Select value={(dirty[u.id]?.deputy_user_id ?? u.deputy_user_id) || "none"} onValueChange={(v) => setDeputy(u, v)}>
                    <SelectTrigger className="h-7 w-28 text-[10px]" data-testid={`deputy-select-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">–</SelectItem>
                      {users.filter((x) => x.id !== u.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                {locs.map((l) => (
                  <td key={l.id} className="px-2 py-2 text-center">
                    <Checkbox checked={assigned(u).includes(l.id)} onCheckedChange={() => toggle(u, l.id)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} disabled={!Object.keys(dirty).length} data-testid="matrix-save-btn">{t("save")}</Button>
    </div>
  );
}

export function DigestTab() {
  const { t } = useApp();
  const [enabled, setEnabled] = useState(true);
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/digest/settings").then((r) => setEnabled(r.data.enabled)).catch(() => {}); }, []);

  const toggleEnabled = async (v) => {
    setEnabled(v);
    await api.put("/digest/settings", { enabled: v });
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post("/digest/send-test", { email });
      toast.success(r.data.sent ? `${t("email_sent_ok")}: ${r.data.to}` : t("email_simulated"));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="digest-tab">
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold flex items-center gap-1.5"><Mail className="h-4 w-4 text-gray-400" />{t("weekly_digest")}</p>
            <p className="text-xs text-gray-400">{t("digest_enabled")}</p>
          </div>
          <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid="digest-enabled-switch" />
        </div>
        <div className="flex gap-2">
          <Input data-testid="digest-test-email-input" type="email" placeholder="webmaster@surplify.de" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white shrink-0" disabled={!email.includes("@") || sending}
            onClick={sendTest} data-testid="digest-send-test-btn">{sending ? "…" : t("send_test")}</Button>
        </div>
        <Button variant="outline" size="sm" data-testid="digest-preview-btn"
          onClick={() => api.get("/digest/preview").then((r) => setPreview(r.data))}>
          <Eye className="h-3.5 w-3.5 mr-1" />{t("preview")}
        </Button>
        {preview && (
          <div className="border border-gray-100 rounded-md p-3 bg-gray-50/50">
            <p className="text-xs font-semibold mb-2">{preview.subject}</p>
            <Textarea readOnly rows={14} className="text-xs font-mono bg-white" value={preview.body} data-testid="digest-preview-body" />
          </div>
        )}
      </div>
    </div>
  );
}

const PERM_MODULES = ["accounts", "contacts", "leads", "opportunities", "quotes", "complaints",
  "activities", "samples", "artworks", "contracts", "plants", "locations", "reports"];
const LEVELS = ["none", "read", "write"];
const SEE_FLAGS = ["see_revenue_qty", "see_wallet", "see_visit_notes", "see_web_research", "see_ai_analysis"];
const VIS_OPTS = ["all", "assigned", "assigned_team"];
const VIS_LBL = { all: "vis_all", assigned: "vis_assigned", assigned_team: "vis_team" };

export function GroupsTab() {
  const { t } = useApp();
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/perm-groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/perm-groups/${editing.id}`, editing);
      else await api.post("/perm-groups", editing);
      toast.success(t("save") + " ✓");
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const newGroup = () => setEditing({
    name: "", default: "write", modules: {}, see_margins: false, location_scoped: false,
    account_visibility: "assigned", unassigned_view: "basic",
    see_revenue_qty: true, see_wallet: true, see_visit_notes: true, see_web_research: true,
    see_ai_analysis: true, can_reassign: false,
  });

  return (
    <div className="space-y-3" data-testid="groups-tab">
      <p className="text-sm text-gray-500">{t("roles_rights")}</p>
      <div className="flex justify-end">
        <Button data-testid="add-group-btn" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full"
          onClick={newGroup}>
          <Plus className="h-4 w-4 mr-1" />{t("add_group")}
        </Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {groups.length === 0 && <p className="p-4 text-sm text-gray-400">–</p>}
        {groups.map((g) => (
          <div key={g.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="perm-group-row">
            <button className="font-medium text-blue-600 hover:underline" onClick={() => setEditing(g)}>{g.name}</button>
            <Badge variant="outline" className="text-[10px]">{t("account_visibility")}: {t(VIS_LBL[g.account_visibility] || "vis_all")}</Badge>
            {g.see_margins && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px]">{t("margins_visible")}</Badge>}
            {g.can_reassign && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 text-[10px]">{t("can_reassign")}</Badge>}
            <button className="ml-auto text-gray-300 hover:text-red-500" onClick={async () => { await api.delete(`/perm-groups/${g.id}`); load(); }}
              data-testid={`delete-group-${g.name}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? t("edit") : t("add_group")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>{t("name")}</Label>
                  <Input data-testid="group-name-input" value={editing.name} onChange={(e) => setEditing((g) => ({ ...g, name: e.target.value }))} /></div>
                <div className="space-y-1"><Label>{t("default_right")}</Label>
                  <Select value={editing.default} onValueChange={(v) => setEditing((g) => ({ ...g, default: v }))}>
                    <SelectTrigger data-testid="group-default-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>{t("account_visibility")}</Label>
                  <Select value={editing.account_visibility || "all"} onValueChange={(v) => setEditing((g) => ({ ...g, account_visibility: v }))}>
                    <SelectTrigger data-testid="group-visibility-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{VIS_OPTS.map((v) => <SelectItem key={v} value={v}>{t(VIS_LBL[v])}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label>{t("unassigned_view")}</Label>
                  <Select value={editing.unassigned_view || "basic"} onValueChange={(v) => setEditing((g) => ({ ...g, unassigned_view: v }))}>
                    <SelectTrigger data-testid="group-unassigned-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">{t("unassigned_basic")}</SelectItem>
                      <SelectItem value="hidden">{t("unassigned_hidden")}</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-2 border border-gray-100 rounded-md p-3">
                <Label className="text-xs uppercase text-gray-500">{t("field_permissions")}</Label>
                {SEE_FLAGS.map((f) => (
                  <label key={f} className="flex items-center justify-between text-sm">
                    <span>{t(f)}</span>
                    <Switch checked={!!editing[f]} onCheckedChange={(v) => setEditing((g) => ({ ...g, [f]: v }))} data-testid={`group-flag-${f}`} />
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.see_margins} onCheckedChange={(v) => setEditing((g) => ({ ...g, see_margins: v }))} data-testid="group-margins-switch" />{t("margins_visible")}</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.location_scoped} onCheckedChange={(v) => setEditing((g) => ({ ...g, location_scoped: v }))} data-testid="group-scoped-switch" />{t("scoped_label")}</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.can_reassign} onCheckedChange={(v) => setEditing((g) => ({ ...g, can_reassign: v }))} data-testid="group-reassign-switch" />{t("can_reassign")}</label>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-gray-500">{t("module_rights")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PERM_MODULES.map((m) => (
                    <div key={m} className="flex items-center justify-between border border-gray-100 rounded-md px-2 py-1.5">
                      <span className="text-xs">{t(m)}</span>
                      <Select value={editing.modules?.[m] || "default"} onValueChange={(v) => setEditing((g) => ({
                        ...g, modules: { ...g.modules, ...(v === "default" ? {} : { [m]: v }) },
                      }))}>
                        <SelectTrigger className="h-7 w-24 text-[10px]" data-testid={`group-module-${m}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">({t("default_right")})</SelectItem>
                          {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="group-save-btn">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AccessTab() {
  const { t } = useApp();
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [grants, setGrants] = useState([]);
  const [log, setLog] = useState([]);
  const [g, setG] = useState({ user_id: "", account_id: "", type: "deputy", start_date: "", end_date: "", reason: "" });
  const [bulk, setBulk] = useState({ owner_id: "", mode: "replace", account_ids: [] });

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/accounts?limit=1000").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/account-grants").then((r) => setGrants(r.data)).catch(() => {});
    api.get("/account-access-log?limit=100").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addGrant = async () => {
    if (!g.user_id || !g.account_id) { toast.error(t("user") + " / " + t("account")); return; }
    try { await api.post("/account-grants", g); toast.success(t("save") + " ✓"); setG({ user_id: "", account_id: "", type: "deputy", start_date: "", end_date: "", reason: "" }); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const delGrant = async (id) => { try { await api.delete(`/account-grants/${id}`); load(); } catch (e) { toast.error(errDetail(e)); } };

  const toggleAcc = (id) => setBulk((b) => ({ ...b, account_ids: b.account_ids.includes(id) ? b.account_ids.filter((x) => x !== id) : [...b.account_ids, id] }));
  const runBulk = async () => {
    if (!bulk.owner_id || bulk.account_ids.length === 0) { toast.error(t("select_accounts")); return; }
    try { const r = await api.post("/accounts/bulk-assign", { owner_ids: [bulk.owner_id], mode: bulk.mode, account_ids: bulk.account_ids }); toast.success(`${r.data.updated} ✓`); setBulk({ owner_id: "", mode: "replace", account_ids: [] }); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-6" data-testid="access-tab">
      {/* Bulk assignment */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <p className="text-sm font-semibold text-[#1B2A4A]">{t("bulk_assign")}</p>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={bulk.owner_id} onValueChange={(v) => setBulk((b) => ({ ...b, owner_id: v }))}>
            <SelectTrigger className="w-56" data-testid="bulk-owner-select"><SelectValue placeholder={t("responsible_reps")} /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={bulk.mode} onValueChange={(v) => setBulk((b) => ({ ...b, mode: v }))}>
            <SelectTrigger className="w-40" data-testid="bulk-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="replace">{t("mode_replace")}</SelectItem><SelectItem value="add">{t("mode_add")}</SelectItem></SelectContent>
          </Select>
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={runBulk} data-testid="bulk-assign-btn">{t("bulk_assign")} ({bulk.account_ids.length})</Button>
        </div>
        <div className="border border-gray-100 rounded-md max-h-40 overflow-y-auto divide-y divide-gray-50">
          {accounts.map((a) => (
            <label key={a.id} className="px-3 py-1.5 flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50" data-testid={`bulk-acc-${a.id}`}>
              <Checkbox checked={bulk.account_ids.includes(a.id)} onCheckedChange={() => toggleAcc(a.id)} />
              <span>{a.name}</span><span className="text-xs text-gray-400 ml-auto">{a.owner_name || "–"}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Grants */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <p className="text-sm font-semibold text-[#1B2A4A]">{t("active_grants")}</p>
        <p className="text-xs text-gray-400">{t("grants_deputy_help")}</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div className="col-span-2"><Label className="text-[10px] text-gray-400">{t("user")}</Label>
            <Select value={g.user_id} onValueChange={(v) => setG((x) => ({ ...x, user_id: v }))}>
              <SelectTrigger className="h-8" data-testid="grant-user-select"><SelectValue placeholder="–" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2"><Label className="text-[10px] text-gray-400">{t("account")}</Label>
            <Select value={g.account_id} onValueChange={(v) => setG((x) => ({ ...x, account_id: v }))}>
              <SelectTrigger className="h-8" data-testid="grant-account-select"><SelectValue placeholder="–" /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("all_accounts_visible")}</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px] text-gray-400">{t("valid_from")}</Label>
            <Input type="date" className="h-8" value={g.start_date} onChange={(e) => setG((x) => ({ ...x, start_date: e.target.value }))} data-testid="grant-start" /></div>
          <div><Label className="text-[10px] text-gray-400">{t("valid_to")}</Label>
            <Input type="date" className="h-8" value={g.end_date} onChange={(e) => setG((x) => ({ ...x, end_date: e.target.value }))} data-testid="grant-end" /></div>
        </div>
        <div className="flex gap-2">
          <Input placeholder={t("reason")} value={g.reason} onChange={(e) => setG((x) => ({ ...x, reason: e.target.value }))} data-testid="grant-reason" />
          <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full shrink-0" onClick={addGrant} data-testid="grant-add-btn"><Plus className="h-4 w-4 mr-1" />{t("add_grant")}</Button>
        </div>
        <div className="divide-y divide-gray-100">
          {grants.length === 0 && <p className="text-sm text-gray-400">{t("no_grants")}</p>}
          {grants.map((gr) => (
            <div key={gr.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`grant-row-${gr.id}`}>
              <Badge className="bg-amber-50 text-amber-800 hover:bg-inherit">{t(gr.type === "exception" ? "exception" : "deputy")}</Badge>
              <span className="font-medium">{gr.user_name}</span><span className="text-gray-400">→</span>
              <span>{gr.account_name || t("all_accounts_visible")}</span>
              <span className="text-xs text-gray-400 ml-auto">{gr.start_date || "…"} → {gr.end_date || "∞"}</span>
              <button className="text-gray-300 hover:text-red-500" onClick={() => delGrant(gr.id)} data-testid={`grant-del-${gr.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Access log */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-2">
        <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5"><Eye className="h-4 w-4 text-gray-400" />{t("access_log")}</p>
        <div className="max-h-64 overflow-y-auto divide-y divide-gray-50" data-testid="access-log-list">
          {log.length === 0 && <p className="text-sm text-gray-400">–</p>}
          {log.map((l) => (
            <div key={l.id} className="py-1.5 flex items-center gap-3 text-xs">
              <span className="font-medium">{l.user_name}</span><span className="text-gray-400">{l.action}</span>
              <span>{l.account_name}</span><span className="text-gray-300 ml-auto font-mono">{(l.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
